import { Eye, EyeOff } from "lucide-react";
import * as React from "react";
import ReactInputMask from "react-input-mask";

import { cn } from "../../lib/utils";
export interface FloatingLabelInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  mask?: string;
}

const FloatingLabelInput = React.forwardRef<
  HTMLInputElement,
  FloatingLabelInputProps
>(({ className, label, type, mask, id: idFromProps, ...props }, ref) => {
  const generatedId = React.useId();
  const inputId = idFromProps ?? generatedId;
  const [showPassword, setShowPassword] = React.useState(false);
  const isPassword = type === "password";

  const input = (
    <input
      id={inputId}
      ref={ref}
      type={isPassword && showPassword ? "text" : type}
      className={cn(
        "peer w-full min-h-11 rounded-md border border-input bg-background px-3 pt-2.5 pb-2 text-sm ring-offset-background placeholder:text-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        isPassword && "pr-10",
        className
      )}
      {...props}
    />
  );

  return (
    <div className="relative">
      {mask ? (
        <ReactInputMask mask={mask} maskChar={null} {...props}>
          {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) =>
            React.cloneElement(input, { ...inputProps, id: inputId, ref })
          }
        </ReactInputMask>
      ) : (
        input
      )}

      <label
        htmlFor={inputId}
        className={cn(
          "pointer-events-none",
          "absolute left-3 top-1/2 z-10 -translate-y-1/2 origin-left bg-background px-1 text-sm text-muted-foreground transition-all duration-150",
          "peer-focus:-top-2 peer-focus:left-3 peer-focus:translate-y-0 peer-focus:scale-[0.85] peer-focus:text-primary",
          "peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:left-3 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:scale-[0.85] peer-[:not(:placeholder-shown)]:text-primary"
        )}
      >
        {label}
      </label>

      {isPassword && !props.disabled && (
        <button
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
          className="absolute right-3 top-1/2 -translate-y-1/2 hover:text-foreground focus:outline-none"
          tabIndex={-1}
        >
          {showPassword ? (
            <EyeOff className="w-4 h-4" />
          ) : (
            <Eye className="w-4 h-4" />
          )}
        </button>
      )}
    </div>
  );
});

FloatingLabelInput.displayName = "FloatingLabelInput";

export { FloatingLabelInput };
